<template>
    <div v-if="recording">
        <template v-if="canStop">
            <button class="btn btn-secondary" @click="stop"><i class="fa fa-circle text-lowercase"></i>
                {{$t('recorder_stop_recording')}}
            </button>
        </template>
        <button v-else class="btn btn-sm btn-secondary" disabled><i class="fa fa-circle text-lowercase blink"></i>
            {{$t('recorder_record')}}
        </button>
    </div>
</template>

<script>
    // Preferred recording formats. iOS Safari only records mp4, older Chrome and Firefox only webm.
    const MIME_TYPES = ['video/mp4', 'video/webm;codecs=vp8,opus', 'video/webm'];

    /**
     * Find the first recording format this browser supports.
     * @return {String} Empty string lets the browser pick its own default.
     */
    function supportedMimeType() {
        if(!window.MediaRecorder || typeof window.MediaRecorder.isTypeSupported !== 'function') {
            return '';
        }
        return MIME_TYPES.find(type => window.MediaRecorder.isTypeSupported(type)) || '';
    }

    export default {
        name: 'sequence-recorder',
        props: {
            canStop: Boolean,
            player: HTMLVideoElement,
            camera: HTMLVideoElement,
        },
        destroyed() {
            // Unbind keyup eventlistener.
            document.body.onkeyup = null;
        },
        data() {
            return {
                mediaRecorder: undefined,
                mediaStream: undefined,
                recordedChunks: undefined,
                recording: false,
                recorded: false,
                timeout: null,
            }
        },
        methods: {
            /**
             * On receive data from recorder.
             * @param {Object} event
             */
            onDataAvailable(event) {
                if(event.data && event.data.size > 0) {
                    this.recordedChunks.push(event.data);
                }
            },
            /**
             * Get the camera stream, requesting it again when iOS has ended its tracks
             * (e.g. after the screen was locked or the app was in the background).
             * @return {Promise<MediaStream>}
             */
            getStream() {
                let stream = window.stream;
                if(stream && stream.getTracks().every(track => track.readyState === 'live')) {
                    return Promise.resolve(stream);
                }
                this.log('Camera stream ended, requesting it again');
                return window.navigator.mediaDevices
                    .getUserMedia({video: {facingMode: "user"}, audio: true})
                    .then(newStream => {
                        window.stream = newStream;
                        return newStream;
                    });
            },
            /**
             * Start recording.
             * @param {Number} ms Duration in milliseconds.
             * @return {Promise} Resolves with the recorded Blob.
             */
            record(ms) {
                return this.getStream().then(stream => new Promise((resolve, reject) => {

                    this.mediaStream = stream;

                    if(this.player) {
                        this.camera.srcObject = this.mediaStream;
                    }

                    this.recordedChunks = [];

                    // Hardcoding a mimeType throws on browsers that do not support it.
                    const mimeType = supportedMimeType();
                    this.mediaRecorder = new window.MediaRecorder(this.mediaStream, mimeType ? {mimeType} : undefined);

                    // Safari can fire `stop` before the last `dataavailable`, so only resolve once both happened.
                    let stopped = false;
                    let finalDataReceived = false;
                    const finish = () => {
                        if(!stopped || !finalDataReceived) return;
                        const type = this.mediaRecorder.mimeType || mimeType || 'video/webm';
                        resolve(new Blob(this.recordedChunks, {type}));
                    };

                    this.mediaRecorder.ondataavailable = (event) => {
                        this.onDataAvailable(event);
                        if(this.mediaRecorder.state === 'inactive') {
                            finalDataReceived = true;
                            finish();
                        }
                    };

                    this.mediaRecorder.onstop = () => {
                        this.log('Stopped recording');
                        stopped = true;
                        finish();
                    };
                    this.mediaRecorder.onerror = reject;
                    this.mediaRecorder.start();
                    this.log('Started recording');
                    this.recording = true;
                    this.timeout = setTimeout(() => {
                        this.mediaRecorder.stop();
                        this.recording = false;
                        this.recorded = true;
                    }, ms)
                }))
            },
            /**
             * Stop the recording manually.
             */
            stop() {
                if(this.timeout) {
                    clearTimeout(this.timeout);
                }
                this.mediaRecorder.stop();
                this.recording = false;
                this.recorded = true;
            }

        }
    }
</script>
